'use client';

import { useState } from 'react';
import { Send, MapPin, Phone, Mail, Clock, MessageCircle, CheckCircle2, TrainFront, Bus, SquareParking } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { SITE } from '@/lib/site';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 실제로는 서버로 데이터를 전송해야 합니다
    console.log('문의 데이터:', formData);

    // localStorage에 임시 저장
    const inquiries = JSON.parse(localStorage.getItem('inquiries') || '[]');
    inquiries.push({
      ...formData,
      id: Date.now().toString(),
      submittedAt: new Date().toISOString()
    });
    localStorage.setItem('inquiries', JSON.stringify(inquiries));

    setSubmitted(true);

    // 3초 후 폼 초기화
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }, 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const q = encodeURIComponent(SITE.address);

  return (
    <>
      <PageHeader en="Contact" title="문의하기" description="궁금하신 사항을 문의해주세요. 빠르게 답변드리겠습니다." />

      <section className="bg-cream pb-20 sm:pb-28">
        <div className="wrap">
          {/* 빠른 연락 */}
          <div className="grid gap-4 md:grid-cols-3">
            <a href={`tel:${SITE.phone}`} className="group flex items-center gap-4 rounded-[28px] bg-white p-6 transition-colors hover:bg-forest">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest-50 text-forest-700 group-hover:bg-cream/10 group-hover:text-sun">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs font-semibold text-muted group-hover:text-white/60">전화 상담</p>
                <p className="text-lg font-bold text-ink group-hover:text-white">{SITE.phone}</p>
              </div>
            </a>
            <a href={`mailto:${SITE.email}`} className="group flex items-center gap-4 rounded-[28px] bg-white p-6 transition-colors hover:bg-forest">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest-50 text-forest-700 group-hover:bg-cream/10 group-hover:text-sun">
                <Mail className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-muted group-hover:text-white/60">이메일</p>
                <p className="truncate text-lg font-bold text-ink group-hover:text-white">{SITE.email}</p>
              </div>
            </a>
            <a href={SITE.kakao} target="_blank" rel="noreferrer" className="flex items-center gap-4 rounded-[28px] bg-[#FEE500] p-6 transition-transform hover:-translate-y-0.5">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#191919]/10 text-[#191919]">
                <MessageCircle className="h-5 w-5 fill-[#191919]" />
              </span>
              <div>
                <p className="text-xs font-semibold text-[#191919]/60">실시간 상담</p>
                <p className="text-lg font-bold text-[#191919]">카카오톡 상담하기</p>
              </div>
            </a>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-12">
            {/* 문의 폼 */}
            <div className="rounded-[32px] bg-white p-7 sm:p-10 lg:col-span-7">
              <h2 className="text-2xl font-bold text-ink">온라인 문의</h2>

              {submitted && (
                <div className="mt-5 flex items-start gap-3 rounded-2xl bg-forest-50 p-5 text-forest">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                  <div>
                    <p className="font-bold">문의가 접수되었습니다!</p>
                    <p className="text-sm text-forest-600">빠른 시일 내에 연락드리겠습니다.</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-7 grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="field-label">이름 <span className="text-forest-600">*</span></span>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="홍길동" className="field" />
                </label>
                <label className="block">
                  <span className="field-label">전화번호 <span className="text-forest-600">*</span></span>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="010-1234-5678" className="field" />
                </label>
                <label className="block">
                  <span className="field-label">이메일 <span className="text-forest-600">*</span></span>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="example@email.com" className="field" />
                </label>
                <label className="block">
                  <span className="field-label">문의 유형 <span className="text-forest-600">*</span></span>
                  <select name="subject" value={formData.subject} onChange={handleChange} required className="field">
                    <option value="">선택해주세요</option>
                    <option value="course">과정 문의</option>
                    <option value="enrollment">등록 문의</option>
                    <option value="schedule">일정 문의</option>
                    <option value="fee">수강료 문의</option>
                    <option value="other">기타 문의</option>
                  </select>
                </label>
                <label className="block sm:col-span-2">
                  <span className="field-label">문의 내용 <span className="text-forest-600">*</span></span>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="문의하실 내용을 자세히 입력해주세요"
                    className="field resize-none"
                  />
                </label>
                <button type="submit" className="btn-coral py-4 text-base sm:col-span-2">
                  <Send className="h-4 w-4" />
                  문의하기
                </button>
              </form>
            </div>

            {/* 운영시간 + 오시는 길 */}
            <div className="space-y-6 lg:col-span-5">
              <div className="rounded-[32px] bg-forest p-7 text-white sm:p-8">
                <p className="flex items-center gap-2 font-serif font-extrabold text-lg text-sun">
                  <Clock className="h-4 w-4" /> Opening hours
                </p>
                <dl className="mt-5 divide-y divide-cream/10">
                  {SITE.hours.map((h) => (
                    <div key={h.day} className="flex justify-between py-3">
                      <dt className="text-white/60">{h.day}</dt>
                      <dd className={`font-bold ${h.time === '휴무' ? 'text-forest-600' : ''}`}>{h.time}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="overflow-hidden rounded-[32px] bg-white">
                <div className="relative h-60">
                  <iframe
                    title="이든 영어학원 위치"
                    src={`https://maps.google.com/maps?q=${q}&z=16&output=embed`}
                    className="absolute inset-0 h-full w-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <div className="p-7 sm:p-8">
                  <h2 className="text-xl font-bold text-ink">오시는 길</h2>
                  <p className="mt-2 flex items-start gap-2 text-muted">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-forest-600" />
                    {SITE.address} {SITE.addressDetail}
                  </p>
                  <ul className="mt-5 space-y-3 text-sm">
                    <li className="flex gap-3">
                      <TrainFront className="h-4 w-4 shrink-0 text-forest-600" />
                      <span><b className="text-ink">지하철</b> <span className="text-muted">2호선 강남역 3번 출구 도보 5분</span></span>
                    </li>
                    <li className="flex gap-3">
                      <Bus className="h-4 w-4 shrink-0 text-forest-600" />
                      <span><b className="text-ink">버스</b> <span className="text-muted">강남역 정류장 하차</span></span>
                    </li>
                    <li className="flex gap-3">
                      <SquareParking className="h-4 w-4 shrink-0 text-forest-600" />
                      <span><b className="text-ink">주차</b> <span className="text-muted">건물 지하 주차장 이용 (2시간 무료)</span></span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
